import { createElement, lazy, Suspense } from 'react';
import type { PageContext } from '../lib/types';
import { usesDesignV2 } from '../features/design-v2/rollout';
// Asset detail (IA §23, §32–34; backend §35, §98–99): BELONGS TO vs USED IN, versions, lineage, safe delete.
import { html, raw, toast } from '../lib/dom';
import * as store from '../store/ui';
import { icon } from '../components/icons';
import { assetFileUrl, assetThumb } from '../components/thumb';
import { tabs, favButton, typeTag, kv, empty, assetCard, json } from '../components/ui';
import { confirmModal, openModal } from '../components/modal';
import { openSafeDelete } from '../components/safe-delete';
import { isReadingRow } from '../api/core';
import { getAsset, ownerLabel, usage, relatedFamily, lineage, SCOPE_LABEL, setAssetStatus, renameAsset, promoteAsset, archiveAsset, ASSET_STATUSES } from '../api/library';
import { getBrand, getWorkspace, getProject, getUser, projectBrands } from '../api/workspace';
import { relTime, shortDate, dateTime, plural } from '../lib/format';

const AssetDetailPage = lazy(() => import('../features/design-v2/LiveAssetDetailPage').then(module => ({ default: module.LiveAssetDetailPage })));

function fileDetails(a?: any) {
  return kv([
    ['Type', html`${typeTag(a.type)}${a.subtype ? html` <span class="mono muted" style="font-size:11px">/ ${a.subtype}</span>` : ''}`],
    a.provider ? ['Provider', a.provider] : null,
    ['Created by', getUser(a.created_by)?.name],
    ['Created', shortDate(a.created_at)],
    ['Updated', relTime(a.updated_at)],
    a.tags?.length ? ['Tags', a.tags.map((t?: any) => html`<span class="chip" style="margin-right:4px">${t}</span>`)] : null,
  ]);
}

function usageTree(u?: any, { full = false }: any = {}) {
  if (u.loading) return empty(u.error ? 'Usage unavailable. Reload to try again.' : 'Loading asset usage…');
  if (!u.nodes) return empty('Not used by any flow yet.');
  return html`<div class="tree">${u.tree.map((p?: any) => html`
    <div class="l1"><a href="#/projects/${p.project.id}">${p.project.name}</a></div>
    ${p.flows.map((f?: any) => html`<div class="l2">└ <a href="#/projects/${p.project.id}/flows/${f.flow.id}">${f.flow.name}</a></div>
      ${full ? f.nodes.map((n?: any) => html`<div class="l3">└ <a href="#/projects/${p.project.id}/flows/${f.flow.id}?node=${n.node.id}">${n.node.name}</a> <span class="chip" style="height:18px">${n.version_ref === 'latest' ? 'latest' : `pinned v${n.version_ref}`}</span></div>`) : ''}`)}`)}</div>`;
}

function definition(a?: any) {
  if (a.definition?.colors) return html`<div class="swatches">${a.definition.colors.map((c?: any) => html`<div class="col" style="gap:4px;align-items:center"><span class="swatch" style="width:44px;height:44px;background:${raw(c)}"></span><span class="mono muted" style="font-size:10px">${c}</span></div>`)}</div>`;
  if (a.definition?.text) return html`<pre class="code" style="white-space:pre-wrap">${a.definition.text}</pre>`;
  if (a.definition?.families) return html`<div class="col">${a.definition.families.map((f?: any) => html`<span style="font-size:22px">${f} <span class="mono muted" style="font-size:11px">Aa Bb 123</span></span>`)}</div>`;
  if (a.config) return json(a.config);
  return null;
}

function renameAssetModal(asset: any, ctx: PageContext) {
  openModal({
    title: 'Rename asset',
    body: html`<form class="form" onsubmit="return false">
      <div class="field"><label for="asset-name">Name</label><input id="asset-name" class="input" name="name" value="${asset.name}" maxlength="255" required autofocus></div>
      <p class="banner error" role="alert" data-rename-error hidden></p>
    </form>`,
    foot: html`<button class="btn ghost" data-close>Cancel</button><button class="btn primary" data-rename-save>Save name</button>`,
    onMount(modal?: any, close?: any) {
      const input = modal.querySelector('[name="name"]');
      const button = modal.querySelector('[data-rename-save]');
      button.addEventListener('click', async () => {
        const error = modal.querySelector('[data-rename-error]');
        error.hidden = true;
        const name = input.value.trim();
        if (!name) { input.focus(); return; }
        button.disabled = true;
        button.textContent = 'Saving…';
        try {
          await renameAsset(asset.id, name);
          close();
          toast('Asset renamed');
          ctx.rerender();
        } catch (cause: any) {
          error.textContent = cause?.message || 'Could not rename asset.';
          error.hidden = false;
        } finally {
          button.disabled = false;
          button.textContent = 'Save name';
        }
      });
    },
  });
}

export function render(ctx: PageContext) {
  if (usesDesignV2(ctx)) return { title: 'Asset', content: createElement(Suspense, { fallback: createElement('div', { role: 'status', className: 'page' }, 'Loading asset…') }, createElement(AssetDetailPage, { context: ctx })) };
  const a = getAsset(ctx.params.assetId);
  if (!a && isReadingRow('assets', ctx.params.assetId)) return { title: 'Asset', body: html`<div class="page">${empty('Loading asset…')}</div>` };
  if (!a) return { title: 'Asset not found', body: html`<div class="page">${empty('This asset was deleted or doesn’t exist.', html`<a class="btn sm" href="#/assets">Back to Assets</a>`)}</div>` };
  if (a.origin === 'remotion-template' || a.data?.origin === 'remotion-template') return {
    title: a.name, body: html`<div class="page"><a class="btn primary" href="#/assets/templates/${a.id}/edit">Edit Remotion template</a></div>`,
    mount() { location.replace('/#/assets/templates/' + a.id + '/edit'); },
  };
  const tab = ctx.query.tab === 'usage' ? 'usage' : 'overview';
  const u = usage(a.id);
  const family = relatedFamily(a);
  const lin = lineage(a);
  const def = definition(a);
  const brandForPromotion = a.scope === 'PROJECT' ? projectBrands(a.project_id)[0] : null;

  const content: any = {
    overview: html`<div class="grid c2" style="align-items:start">
        <section class="card pad-lg col" style="gap:12px"><div class="eyebrow">File details</div>${fileDetails(a)}</section>
        <section class="card pad-lg col" style="gap:12px"><div class="row"><span class="eyebrow">Used in</span><span class="spacer"></span><a class="mono" style="font-size:11px;color:var(--fern)" href="#/assets/${a.id}?tab=usage">Details →</a></div>
          <div class="row mono" style="gap:18px;font-size:12px"><span><strong style="font-size:20px;font-weight:400">${u.loading ? '…' : u.projects}</strong> projects</span><span><strong style="font-size:20px;font-weight:400">${u.loading ? '…' : u.flows}</strong> flows</span><span><strong style="font-size:20px;font-weight:400">${u.loading ? '…' : u.nodes}</strong> nodes</span></div>
          ${usageTree(u)}
          <p class="muted" style="font-size:12px">Using an asset never changes who owns it.</p></section>
      </div>
      ${def ? html`<section class="card pad-lg col" style="gap:12px"><div class="eyebrow">Definition</div>${def}</section>` : ''}
      ${lin ? html`<section class="card pad-lg col" style="gap:12px"><div class="eyebrow">Generated by</div>${kv([
        // A project, flow or run can be deleted or not loaded yet; show what is known instead of failing the page.
        ['Project', lin.project ? html`<a href="#/projects/${lin.project.id}" style="color:var(--fern)">${lin.project.name}</a>` : html`<span class="muted">—</span>`],
        ['Flow', lin.flow && lin.project ? html`<a href="#/projects/${lin.project.id}/flows/${lin.flow.id}" style="color:var(--fern)">${lin.flow.name}</a>` : lin.flow?.name || html`<span class="muted">—</span>`],
        ['Run', lin.run ? html`<a href="#/runs/${lin.run.id}${lin.node_run_id ? `?nr=${lin.node_run_id}` : ''}" style="color:var(--fern)">#${lin.run.number}</a>` : html`<span class="muted">—</span>`],
        ['Model', lin.model], ['Date', lin.generated_at ? dateTime(lin.generated_at) : html`<span class="muted">—</span>`],
      ])}</section>` : ''}
      ${family.length ? html`<section class="section"><h2>Character family</h2><div class="grid auto-sm">${family.map((x?: any) => assetCard(x, x.status))}</div></section>` : ''}`,
    usage: html`<section class="card pad-lg col" style="gap:14px">
        <div class="row wrap" style="gap:10px"><span class="chip">${plural(u.projects, 'project')}</span><span class="chip">${plural(u.flows, 'flow')}</span><span class="chip">${plural(u.nodes, 'node')}</span><span class="spacer"></span><span class="muted" style="font-size:12px">Needed for safe delete and impact analysis.</span></div>
        ${usageTree(u, { full: true })}
      </section>`,
  };

  const body = html`<div class="page">
    <a data-back class="btn ghost sm" href="#/assets" style="align-self:flex-start">${icon('back')}Back</a>
    <div style="display:grid;grid-template-columns:minmax(260px,420px) minmax(0,1fr);gap:28px;align-items:start">
      ${assetThumb(a, 'thumb hero')}
      <div class="col" style="gap:12px">
        <div class="row wrap" style="gap:8px">${typeTag(a.type)}<span class="chip">${SCOPE_LABEL[a.scope]} · ${ownerLabel(a)}</span>${a.current_version > 1 ? html`<span class="chip">v${a.current_version}</span>` : ''}</div>
        <div class="row wrap" style="gap:6px"><h1>${a.name}</h1><button class="btn sm ghost" data-rename>Rename</button>${favButton('asset', a.id)}</div>
        ${a.description ? html`<p class="lede" style="margin:0">${a.description}</p>` : ''}
        <div class="row wrap" style="gap:10px">
          <label class="row" style="gap:6px"><span class="eyebrow">Status</span><select class="select sm" data-asset-status>${ASSET_STATUSES.map((s?: any) => html`<option ${raw(s === a.status ? 'selected' : '')}>${s}</option>`)}</select></label>
          <span class="mono muted" style="font-size:11px">${u.loading ? (u.error ? 'Usage unavailable' : 'Checking usage…') : u.nodes ? `Used in ${plural(u.flows, 'flow')}` : 'Unused'}</span>
        </div>
        <div class="row wrap">
          ${assetFileUrl(a) ? html`<a class="btn" href="${assetFileUrl(a)}" target="_blank" rel="noopener noreferrer">${icon('external')}Open file</a>` : ''}
          ${brandForPromotion ? html`<button class="btn" data-promote>${icon('brands')}Promote to ${brandForPromotion.name} brand</button>` : ''}
          ${a.status === 'Archived' ? html`<button class="btn" data-unarchive>Restore</button>` : html`<button class="btn ghost" data-archive>${icon('archive')}Archive</button>`}
          <button class="btn ghost" data-delete style="color:var(--error)">${icon('trash')}Delete</button>
        </div>
        ${a.status === 'Archived' ? html`<div class="banner info">${icon('archive')}Archived — hidden from pickers, existing flows keep working.</div>` : ''}
      </div>
    </div>
    ${tabs([['overview', 'Overview'], ['usage', 'Used in', u.loading ? '…' : u.nodes]], tab)}
    ${content[tab]}
  </div>`;

  return {
    title: a.name, body,
    mount(root?: any) {
      store.touchRecent('asset', a.id);
      const on = (sel?: any, fn?: any) => root.querySelector(sel)?.addEventListener('click', fn);
      on('[data-rename]', () => renameAssetModal(a, ctx));
      root.querySelector('[data-asset-status]')?.addEventListener('change', (e?: any) => { setAssetStatus(a.id, e.target.value); toast(`Status → ${e.target.value}`); ctx.rerender(); });
      on('[data-promote]', () => confirmModal({
        title: `Promote to ${brandForPromotion.name}?`, text: 'The asset moves from project scope to the brand resources in Assets and becomes available to every project of this brand. Existing usages stay intact.', confirmLabel: 'Promote',
        onConfirm: () => { promoteAsset(a.id, brandForPromotion.id); toast(`Now a ${brandForPromotion.name} brand asset`); ctx.rerender(); },
      }));
      on('[data-archive]', () => { archiveAsset(a.id); toast('Archived'); ctx.rerender(); });
      on('[data-unarchive]', () => { setAssetStatus(a.id, 'Approved'); toast('Restored'); ctx.rerender(); });
      on('[data-delete]', () => openSafeDelete(a, { onDone: (r?: any) => { if (r === 'deleted') location.hash = '/assets'; else ctx.rerender(); } }));
    },
  };
}
